import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react'
import { createPortal } from 'react-dom'
import { useFullscreenHost } from '@/lib/fullscreen'
import { coarsePointer } from '@/lib/mobile'
import { cx } from '@/lib/cx'
import { installTooltips, tooltipDelay, warmTooltip, type TooltipTarget } from '@/lib/tooltips'
import styles from './Tooltip.module.css'

/** Тот же пузырь у явных Tooltip и у подписей data-tooltip. */
export function TooltipBubble({ anchor, content, id }: { anchor: HTMLElement; content: ReactNode; id: string }) {
  const bubble = useRef<HTMLDivElement>(null)
  const host = useFullscreenHost()
  useLayoutEffect(() => {
    const names = new Set((anchor.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean))
    names.add(id)
    anchor.setAttribute('aria-describedby', [...names].join(' '))
    return () => {
      const remaining = (anchor.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((name) => name && name !== id)
      if (remaining.length) anchor.setAttribute('aria-describedby', remaining.join(' '))
      else anchor.removeAttribute('aria-describedby')
    }
  }, [anchor, id])
  useLayoutEffect(() => {
    const a = anchor.getBoundingClientRect()
    const el = bubble.current
    if (!el) return
    const win = anchor.ownerDocument.defaultView!
    const w = el.offsetWidth
    const h = el.offsetHeight
    const left = Math.max(8, Math.min(a.left + a.width / 2 - w / 2, win.innerWidth - w - 8))
    const above = a.top - h - 10 >= 8
    el.style.left = `${left}px`
    el.style.top = `${Math.max(8, Math.min(above ? a.top - h - 10 : a.bottom + 10, win.innerHeight - h - 8))}px`
    el.style.setProperty('--arrow-x', `${Math.max(12, Math.min(a.left + a.width / 2 - left, w - 12))}px`)
    el.dataset.side = above ? 'top' : 'bottom'
    el.style.visibility = 'visible'
  }, [anchor, content, host])
  return createPortal(
    <div ref={bubble} id={id} role="tooltip" className={cx(styles.bubble, 'klichat-popover')} style={{ visibility: 'hidden', left: 0, top: 0 }}>
      {content}
      <span className={styles.arrow} aria-hidden="true" />
    </div>,
    host ?? anchor.ownerDocument.body,
  )
}

/** Подключается один раз у корня, включая окна и страницы за пределами App. */
export function TooltipProvider() {
  const id = useId()
  const [target, setTarget] = useState<TooltipTarget | null>(null)
  useEffect(() => installTooltips(document, setTarget), [])
  return target ? <TooltipBubble anchor={target.element} content={target.content} id={id} /> : null
}

interface Props {
  /** Что показать: строка или карточка (значок: картинка крупно, название, пояснение). */
  content: ReactNode
  children: ReactNode
  /** Имя для чтения с экрана: значки — картинки, у обёртки своего текста нет. */
  label?: string | undefined
  className?: string | undefined
  /** Дочерняя кнопка получает фокус и нажатие сама, без второй остановки Tab на обёртке. */
  interactive?: boolean | undefined
}

/**
 * Быстрая подсказка, как в Discord: тёмный пузырь со стрелкой над элементом. Системная (title)
 * появлялась через секунду и выглядела чужой. Открывается почти сразу; если только что была
 * открыта соседняя — мгновенно, поэтому по ряду значков можно вести мышью. Фокус с клавиатуры
 * тоже открывает её; пальцем — нажатие.
 */
export function Tooltip({ content, children, label, className, interactive = false }: Props) {
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const id = useId()
  const [open, setOpen] = useState(false)

  const show = (e: SyntheticEvent) => {
    window.clearTimeout(timer.current)
    const wait = tooltipDelay(e.timeStamp)
    timer.current = window.setTimeout(() => setOpen(true), wait)
  }
  const hide = (e: SyntheticEvent) => {
    window.clearTimeout(timer.current)
    if (open) warmTooltip(e.timeStamp)
    setOpen(false)
  }

  useEffect(() => () => window.clearTimeout(timer.current), [])

  // прокрутили или сменили размер окна — подсказка остаётся у старого места; проще убрать
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') close() }
    document.addEventListener('scroll', close, true)
    document.addEventListener('keydown', escape, true)
    document.addEventListener('pointerdown', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('scroll', close, true)
      document.removeEventListener('keydown', escape, true)
      document.removeEventListener('pointerdown', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <>
      <span
        ref={setAnchor}
        className={cx(styles.anchor, className)}
        data-tooltip-owned=""
        tabIndex={interactive ? undefined : 0}
        role={label ? 'img' : undefined}
        aria-label={label}
        onMouseEnter={coarsePointer ? undefined : show}
        onMouseLeave={coarsePointer ? undefined : hide}
        onFocus={show}
        onBlur={hide}
        onClick={coarsePointer && !interactive ? () => setOpen((v) => !v) : undefined}
      >
        {children}
      </span>
      {open && anchor ? <TooltipBubble anchor={anchor} content={content} id={id} /> : null}
    </>
  )
}
